import { OfflineRecord, OfflineParsedQuery, SearchResult } from "./types";
import { tokenize, toAscii } from "./shared/tokenizer";

interface IndexedRecord extends OfflineRecord {
  tokenSet: Set<string>;
  asciiTokenSet: Set<string>;
  searchTokens: string[];
}

export class OfflineSearchEngine {
  private records: Map<string, IndexedRecord> = new Map();
  private inverted: Map<string, Set<string>> = new Map();
  private arrangementWho: Map<string, Set<string>> = new Map();
  private arrangementWhat: Map<string, Set<string>> = new Map();
  private dateList: { id: string; date: number }[] = [];

  buildIndex(records: OfflineRecord[]) {
    this.records.clear();
    this.inverted.clear();
    this.arrangementWho.clear();
    this.arrangementWhat.clear();
    this.dateList = [];

    for (const r of records) {
      const searchTokens = new Set<string>();
      const add = (s?: string) => {
        if (!s) return;
        for (const t of tokenize(s)) {
          if (t.length > 1) {
            searchTokens.add(t);
            searchTokens.add(toAscii(t));
          }
        }
      };

      add(r.title);
      add(r.shortSummary);
      add(r.transcript);
      add(r.employeeName);
      add(r.topic);
      add(r.location);

      for (const arr of r.arrangements) {
        add(arr.who);
        add(arr.what);
        add(arr.deadlineDate);
      }

      if (r.tokens) {
        for (const t of r.tokens) searchTokens.add(toAscii(t));
      }

      const tokenSet = new Set<string>(searchTokens);
      const asciiTokenSet = new Set<string>([...tokenSet].map(toAscii));

      const indexed: IndexedRecord = { ...r, tokenSet, asciiTokenSet, searchTokens: [...tokenSet] };
      this.records.set(r.id, indexed);

      this.dateList.push({ id: r.id, date: new Date(r.recordDate).getTime() });

      for (const t of tokenSet) {
        const key = toAscii(t);
        if (!this.inverted.has(key)) this.inverted.set(key, new Set());
        this.inverted.get(key)!.add(r.id);
      }

      for (const arr of r.arrangements) {
        if (arr.who) {
          const n = norm(arr.who);
          if (!this.arrangementWho.has(n)) this.arrangementWho.set(n, new Set());
          this.arrangementWho.get(n)!.add(r.id);
        }
        if (arr.what) {
          const words = tokenize(arr.what).filter(w => w.length > 1);
          for (const w of words) {
            const n = toAscii(w);
            if (!this.arrangementWhat.has(n)) this.arrangementWhat.set(n, new Set());
            this.arrangementWhat.get(n)!.add(r.id);
          }
        }
      }
    }

    this.dateList.sort((a, b) => b.date - a.date);
  }

  search(parsed: OfflineParsedQuery): SearchResult[] {
    let candidateIds = new Set<string>(this.records.keys());

    if (parsed.recordType) {
      candidateIds = new Set([...candidateIds].filter(id => this.records.get(id)!.recordType === parsed.recordType));
    }

    if (parsed.person) {
      const personNorm = norm(parsed.person);
      candidateIds = new Set([...candidateIds].filter(id => {
        const rec = this.records.get(id)!;
        const recNorm = norm(rec.employeeName);
        // dopasowanie po pierwszych 3 znakach (łapie odmiany: Markiem -> Marek)
        const prefixMatch = personNorm.length >= 3 && recNorm.length >= 3 &&
          (personNorm.startsWith(recNorm.slice(0, 3)) || recNorm.startsWith(personNorm.slice(0, 3)));
        return prefixMatch || recNorm.includes(personNorm) || personNorm.includes(recNorm);
      }));
    }

    if (parsed.dateStart || parsed.dateEnd) {
      const start = parsed.dateStart ? new Date(parsed.dateStart).getTime() : -Infinity;
      const end = parsed.dateEnd ? new Date(parsed.dateEnd).getTime() : Infinity;
      candidateIds = new Set([...candidateIds].filter(id => {
        const d = new Date(this.records.get(id)!.recordDate).getTime();
        return d >= start && d <= end;
      }));
    }

    if (parsed.locationFilter) {
      const loc = toAscii(parsed.locationFilter);
      candidateIds = new Set([...candidateIds].filter(id => {
        const rec = this.records.get(id)!;
        return rec.location && toAscii(rec.location).includes(loc);
      }));
    }

    if (parsed.isArrangementQuery && parsed.topicFilter) {
      const topic = toAscii(parsed.topicFilter);
      const arrIds = new Set<string>();
      for (const [what, ids] of this.arrangementWhat) {
        if (what.includes(topic)) {
          for (const id of ids) arrIds.add(id);
        }
      }
      for (const [who, ids] of this.arrangementWho) {
        if (who.includes(topic)) {
          for (const id of ids) arrIds.add(id);
        }
      }
      if (arrIds.size > 0) {
        candidateIds = new Set([...candidateIds].filter(id => arrIds.has(id)));
      }
    }

    const scored: SearchResult[] = [];
    const queryTokens = new Set<string>();
    for (const t of parsed.tokens) queryTokens.add(toAscii(t));
    if (parsed.topicFilter) {
      for (const t of tokenize(parsed.topicFilter)) queryTokens.add(toAscii(t));
    }
    if (parsed.person) {
      queryTokens.add(toAscii(parsed.person));
    }

    for (const id of candidateIds) {
      const rec = this.records.get(id)!;
      let score = 0;

      if (parsed.person && norm(rec.employeeName) === norm(parsed.person)) {
        score += 8;
      }

      if (parsed.topicFilter && rec.topic && toAscii(rec.topic).includes(toAscii(parsed.topicFilter))) {
        score += 4;
      }

      if (parsed.locationFilter && rec.location && toAscii(rec.location).includes(toAscii(parsed.locationFilter))) {
        score += 4;
      }

      for (const qt of queryTokens) {
        if (qt.length <= 1) continue;
        const inTokens = rec.tokenSet.has(qt) || rec.asciiTokenSet.has(qt);
        const inTitle = toAscii(rec.title).includes(qt);
        const inSummary = toAscii(rec.shortSummary).includes(qt);
        const inTranscript = toAscii(rec.transcript).includes(qt);

        if (inTokens) score += 1;
        if (inTitle) score += 2;
        if (inSummary) score += 1;
        if (inTranscript) score += 0.5;

        for (const arr of rec.arrangements) {
          if (arr.who && toAscii(arr.who).includes(qt)) score += 1.5;
          if (arr.what && toAscii(arr.what).includes(qt)) score += 1.5;
        }
      }

      if (score > 0) {
        scored.push({
          id: rec.id,
          title: rec.title,
          shortNote: rec.shortSummary,
          score: Math.round(score * 100) / 100
        });
      }
    }

    // fallback: jesli brak trafien, posortuj po dacie malejaco
    if (scored.length === 0 && candidateIds.size > 0) {
      for (const id of candidateIds) {
        const rec = this.records.get(id)!;
        scored.push({
          id: rec.id,
          title: rec.title,
          shortNote: rec.shortSummary,
          score: 0
        });
      }
      scored.sort((a, b) => {
        const da = new Date(this.records.get(a.id)!.recordDate).getTime();
        const db = new Date(this.records.get(b.id)!.recordDate).getTime();
        return db - da;
      });
      return scored.slice(0, parsed.limit);
    }

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, parsed.limit);
  }
}

function norm(s: string): string {
  return toAscii(s.toLowerCase().trim());
}
